import { i18n } from "@tellescope/i18n";
import {
  TellescopeModelFromName,
  useAuth,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
} from "@tellescope/tanstack-query";
import { type VirtualizedColumnDef, useUncontrolledState, VirtualizedDataTableProps } from "@tellescope/ui";
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import { useTableViewConfig } from "./useTableViewConfig";
import { DEFAULT_TICKETS_VIEW, useTicketsDefaultViewShallowStore } from "../store";
import { NO_VALUE_OPERATIONS } from "@tellescope/utils";
import { type TicketFilters } from "../components/tickets/ticketFilters";
import { COMPLETED_STAGE, NOT_STARTED_STAGE } from "../components/tickets/ticketFilterOptions";
import { ActionsCell, DateCell, EnduserCell, PriorityCell } from "../components/tickets/cells";
import { EnduserFieldCell, TextCell } from "../components/tables";
import { StageCell } from "../components/tickets/cells/StageCell";
import { DueDateCell } from "../components/tickets/cells/DueDateCell";
import { UserCell } from "../components/tickets/cells/UserCell";
import { TicketTagsCell } from "../components/tickets/cells/TicketTagsCell";
import { useTableView } from "./useTableView";
import { ALL_ENDUSER_FIELDS_TO_DISPLAY_NAME } from "@tellescope/constants";
import { useTranslation } from "react-i18next";

type TicketRecord = TellescopeModelFromName<"tickets">;
type TicketColumnDef = VirtualizedColumnDef<TicketRecord>;

// Partial column config without width (width comes from view configuration)
type PartialTicketColumnDef<Value = unknown> = Partial<TicketColumnDef> & {
  label?: string;
  cell?: TicketColumnDef["cell"];
} & (
    | {
        accessorKey?: keyof TicketRecord;
      }
    | {
        accessorFn: (originalRow: TicketRecord, index: number) => Value;
      }
  );

export const getBuiltInFieldToColumnConfigMap: () => Record<string, PartialTicketColumnDef> = () => ({
  Details: {
    accessorKey: "title",
    label: i18n.t("Ticket", { ns: "tickets" }),
    cell: ({ row }) => (
      <TextCell className={row.original.closedAt ? "line-through" : undefined}>{row.original.title}</TextCell>
    ),
  },
  Description: {
    accessorKey: "message",
    label: i18n.t("Description", { ns: "tickets" }),
    cell: ({ row }) => <TextCell className="text-muted-foreground">{row.original.message}</TextCell>,
  },
  Created: {
    accessorKey: "createdAt",
    label: i18n.t("Opened", { ns: "tickets" }),
    cell: ({ row }) => <DateCell timestamp={row.original.createdAt} />,
  },
  Stage: {
    accessorKey: "stage",
    label: i18n.t("Stage", { ns: "tickets" }),
    cell: ({ row }) => {
      return (
        <StageCell
          stage={row.original.closedAt ? "Completed" : row.original.stage}
          ticketId={row.original.id}
          disableChangeStage={!!row.original.closedAt}
        />
      );
    },
  },
  Priority: {
    accessorKey: "priority",
    label: i18n.t("Priority", { ns: "tickets" }),
    cell: ({ row }) => <PriorityCell priority={row.original.priority} ticketId={row.original.id} />,
  },
  "Due Date": {
    accessorKey: "dueDateInMS",
    label: i18n.t("Due Date", { ns: "tickets" }),
    cell: ({ row }) => (
      <DueDateCell
        isComplete={!!row.original.closedAt}
        dueDateInMS={row.original.dueDateInMS}
        ticketId={row.original.id}
      />
    ),
  },
  Owner: {
    accessorKey: "owner",
    label: i18n.t("Owner", { ns: "tickets" }),
    cell: ({ row }) => <UserCell userId={row.original.owner || ""} ticketId={row.original.id} avatarOnly />,
  },
  Enduser: {
    accessorKey: "enduserId",
    label: i18n.t("Patient", { ns: "tickets" }),
    cell: ({ row }) => (
      <EnduserCell
        enduserId={row.original.enduserId || ""}
        ticketId={row.original.id}
        avatarOnly
        variant="open-in-sidebar"
      />
    ),
  },
  Tags: {
    accessorKey: "tags",
    label: i18n.t("Tags", { ns: "tickets" }),
    cell: ({ row }) => <TicketTagsCell ticketId={row.original.id} tags={row.original.tags ?? []} />,
  },
  Actions: {
    align: "right",
    sortable: false,
    label: i18n.t("Actions", { ns: "tickets" }),
    cell: ({ row }) => <ActionsCell ticket={row.original} />,
  },
  index: {
    visible: false,
  },
});

// Base enduser field configs (without prefix)
const BASE_ENDUSER_FIELD_CONFIGS: Record<string, PartialTicketColumnDef> = Object.fromEntries(
  (
    [
      "id",
      "assignedTo",
      "fname",
      "lname",
      "mname",
      "email",
      "phone",
      "landline",
      "dateOfBirth",
      "height",
      "weight",
      "gender",
      "tags",
      "journeys",
      "addressLineOne",
      "addressLineTwo",
      "city",
      "state",
      "zipCode",
      "timezone",
      "externalId",
      "source",
      "relationships",
      "accessTags",
      "unsubscribedFromMarketing",
      "unsubscribedFromPhones",
      "athenaDepartmentId",
      "termsVersion",
      "customTypeId",
    ] as const
  ).map((field) => [
    field,
    {
      accessorKey: "enduserId",
      label: i18n.t("Patient {{field}}", { field: ALL_ENDUSER_FIELDS_TO_DISPLAY_NAME[field], ns: "tickets" }),
      cell: ({ row }: { row: { original: TicketRecord } }) => (
        <EnduserFieldCell enduserId={row.original.enduserId} fieldSelector={field} />
      ),
    },
  ])
);

// Create config map with "enduser." prefix for use in fieldToColumnConfigMap
export const ENDUSER_FIELD_TO_COLUMN_CONFIG_MAP: Record<string, PartialTicketColumnDef> = Object.fromEntries(
  Object.entries(BASE_ENDUSER_FIELD_CONFIGS).map(([key, config]) => [`enduser.${key}`, config])
);

// All available columns from the default view (always shown in toggle menu)
export const AVAILABLE_DEFAULT_TICKET_VIEW_COLUMNS: VirtualizedColumnDef<TicketRecord>[] = (() => {
  const mappedColumns = DEFAULT_TICKETS_VIEW.columns.reduce<VirtualizedColumnDef<TicketRecord>[]>((acc, column) => {
    if (column.field === "index") return acc;
    const config = getBuiltInFieldToColumnConfigMap()[column.field];
    if (config) {
      acc.push({
        ...config,
        id: column.field,
      });
      return acc;
    }
    // Fallback for unknown fields
    acc.push({
      id: column.field,
      label: column.field,
      cell: ({ row }) =>
        column.field in row.original ? (
          <TextCell>{String(row.original[column.field as keyof typeof row.original] ?? "")}</TextCell>
        ) : null,
    });
    return acc;
  }, []);

  // Ensure Actions column is always last
  const actionsIndex = mappedColumns.findIndex((col) => col.id === "Actions");
  if (actionsIndex !== -1) {
    const actionsColumn = mappedColumns[actionsIndex];
    const otherColumns = mappedColumns.filter((_, idx) => idx !== actionsIndex);
    return actionsColumn ? [...otherColumns, actionsColumn] : mappedColumns;
  }
  return mappedColumns;
})();

export const AVAILABLE_ENDUSER_TICKET_VIEW_COLUMNS: VirtualizedColumnDef<TicketRecord>[] = Object.entries(
  ENDUSER_FIELD_TO_COLUMN_CONFIG_MAP
).map<VirtualizedColumnDef<TicketRecord>>(([fieldKey, config]) => ({
  ...config,
  id: fieldKey, // fieldKey already has "enduser." prefix
  // Don't set visible here - let columnVisibility prop control it
}));

// Base static columns (excluding custom fields which are generated dynamically)
export const BASE_TICKET_VIEW_COLUMNS: VirtualizedColumnDef<TicketRecord>[] = (() => {
  const combined = [...AVAILABLE_DEFAULT_TICKET_VIEW_COLUMNS, ...AVAILABLE_ENDUSER_TICKET_VIEW_COLUMNS];

  // Ensure Actions column is always last
  const actionsIndex = combined.findIndex((col) => col.id === "Actions");
  if (actionsIndex !== -1) {
    const actionsColumn = combined[actionsIndex];
    const otherColumns = combined.filter((_, idx) => idx !== actionsIndex);
    return actionsColumn ? [...otherColumns, actionsColumn] : combined;
  }
  return combined;
})();

export interface UseTicketTableArgs {
  userId?: string;
  viewId?: string;
  setViewId?: Dispatch<SetStateAction<string>>;
  pageSize?: number;
  setPageSize?: Dispatch<SetStateAction<number>>;
  pageIndex?: number;
  setPageIndex?: Dispatch<SetStateAction<number>>;
  onRowClick?: VirtualizedDataTableProps<TicketRecord>["onRowClick"];
  queueId?: string;
}

export const useTicketTable = ({
  userId: providedUserId,
  viewId: providedViewId,
  setViewId: providedSetViewId,
  pageSize: providedPageSize,
  setPageSize: providedSetPageSize,
  pageIndex: providedPageIndex,
  setPageIndex: providedSetPageIndex,
  onRowClick,
  queueId,
}: UseTicketTableArgs = {}) => {
  const { t } = useTranslation("tickets");
  const { selectedOrganization: organization, currentUser } = useAuth();
  const userId = providedUserId || currentUser.id || "";
  const [pageSize, setPageSize] = useUncontrolledState({
    state: providedPageSize ?? 25,
    setState: providedSetPageSize,
  });
  const [pageIndex, setPageIndex] = useUncontrolledState({
    state: providedPageIndex ?? 0,
    setState: providedSetPageIndex,
  });
  const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>([]);
  const [hasSeletedAll, setHasSelectedAll] = useState(false);

  const updateTicketIndexesMutation = useTellescopeMutation({
    model: "tickets",
    method: "update_indexes",
  });

  const { defaultViewByUserId, setUserDefaultView } = useTicketsDefaultViewShallowStore(
    "defaultViewByUserId",
    "setUserDefaultView"
  );

  // Create column config map including custom fields
  const customFieldConfigMap = useMemo(() => {
    const customFields = organization?.settings?.endusers?.customFields as
      | Array<{ field: string; type?: string }>
      | undefined;
    if (!customFields || !Array.isArray(customFields)) {
      return {};
    }

    const configMap: Record<string, PartialTicketColumnDef> = {};
    customFields.forEach((customField) => {
      const fieldKey = `enduser.custom.${customField.field}`;
      configMap[fieldKey] = {
        accessorKey: "enduserId",
        label: t("Patient {{field}}", { field: customField.field, ns: "tickets" }),
        cell: ({ row }) => (
          <EnduserFieldCell
            enduserId={row.original.enduserId}
            fieldSelector={(enduser) => (enduser.fields as Record<string, unknown> | undefined)?.[customField.field]}
          />
        ),
      };
    });
    return configMap;
  }, [organization?.settings?.endusers?.customFields, t]);

  const combinedFieldToColumnConfigMap = useMemo(
    () => ({
      ...getBuiltInFieldToColumnConfigMap(),
      ...ENDUSER_FIELD_TO_COLUMN_CONFIG_MAP,
      ...customFieldConfigMap,
    }),
    [customFieldConfigMap]
  );

  const { views, selectedView, columns, viewId, setViewId, sorting, setSorting, isLoadingViews, isDefaultView } =
    useTableView<TicketRecord>({
      page: "T2-Tickets",
      defaultView: defaultViewByUserId[userId] ?? DEFAULT_TICKETS_VIEW,
      fieldToColumnConfigMap: combinedFieldToColumnConfigMap,
      fallbackCell: ({ row }) => {
        const value = row.original[row.id as keyof TicketRecord];
        if (!value) return null;
        return <TextCell>{String(value ?? "")}</TextCell>;
      },
      viewId: providedViewId,
      setViewId: providedSetViewId,
      sortable: !queueId,
      enableIndexColumn: true,
    });

  const activeFilters = useMemo(() => {
    return selectedView.filter ?? {};
  }, [selectedView.filter]);

  const hasExplicitClosedFilter =
    (!!activeFilters.status && activeFilters.status !== "all") ||
    activeFilters.closedDateRange?.from !== undefined ||
    activeFilters.closedDateRange?.to !== undefined;

  const stageFilter = useMemo(() => {
    const selected: string[] = activeFilters.stages ?? [];
    const includesCompleted = selected.includes(COMPLETED_STAGE);
    const openStages = selected.filter((stage) => stage !== COMPLETED_STAGE);
    const isMixedSelection = includesCompleted && openStages.length > 0;
    const openStageValues: (string | null)[] = openStages.includes(NOT_STARTED_STAGE)
      ? [...openStages, null, ""]
      : openStages;

    return {
      isActive: selected.length > 0,
      isMixedSelection,
      openStages,
      openStageValues: isMixedSelection ? [] : openStageValues,
      closedAtExists: isMixedSelection ? undefined : includesCompleted,
    };
  }, [activeFilters.stages]);

  const ticketsQueryFilter = useMemo(() => {
    if (queueId)
      return {
        queueId,
      };

    const f: Record<string, unknown> = {};

    // Owner: default view always filters to current user; named views use the configured owner filter
    if (isDefaultView) {
      f.owner = userId;
    } else if (activeFilters.ownerIds && activeFilters.ownerIds.length > 0) {
      f.owner = { _in: activeFilters.ownerIds };
    }

    // Status (open/closed)
    if (activeFilters.status === "open") {
      f.closedAt = { _exists: false };
    } else if (activeFilters.status === "closed") {
      f.closedAt = { _exists: true };
    }

    // Due date range
    if (activeFilters.dueDateRange?.from !== undefined || activeFilters.dueDateRange?.to !== undefined) {
      const dueDateFilter: Record<string, number> = {};
      if (activeFilters.dueDateRange?.from !== undefined) dueDateFilter._gte = activeFilters.dueDateRange.from;
      if (activeFilters.dueDateRange?.to !== undefined) dueDateFilter._lte = activeFilters.dueDateRange.to;
      f.dueDateInMS = dueDateFilter;
    }

    // Closed date range (closedAt is stored as Date; we filter on updatedAt or use a custom approach)
    // Note: the API filter on closedAt expects a Date/timestamp value for _gte/_lte operators
    if (activeFilters.closedDateRange?.from !== undefined || activeFilters.closedDateRange?.to !== undefined) {
      const closedAtFilter: Record<string, unknown> = { _exists: true };
      if (activeFilters.closedDateRange?.from !== undefined) {
        closedAtFilter._gte = new Date(activeFilters.closedDateRange.from);
      }
      if (activeFilters.closedDateRange?.to !== undefined) {
        closedAtFilter._lte = new Date(activeFilters.closedDateRange.to);
      }
      f.closedAt = closedAtFilter;
    }

    if (stageFilter.isActive) {
      if (stageFilter.openStageValues.length > 0) {
        f.stage = { _in: stageFilter.openStageValues };
      }
      if (!hasExplicitClosedFilter && stageFilter.closedAtExists !== undefined) {
        f.closedAt = { _exists: stageFilter.closedAtExists };
      }
    }

    // Tags filter
    if (activeFilters.tags && activeFilters.tags.tags.length > 0) {
      const { tags, behavior } = activeFilters.tags;
      if (behavior === "match_all") {
        f.tags = { _all: tags };
      } else if (behavior === "match_any") {
        f.tags = { _in: tags };
      } else {
        // match_none
        f.tags = { _nin: tags };
      }
    }

    // Freeform filters
    for (const ff of activeFilters.freeform ?? []) {
      const op = ff.operation;
      if (NO_VALUE_OPERATIONS.includes(op)) {
        f[ff.field] = op === "is_set" ? { _exists: true } : { _exists: false };
      } else if (op === "equals") {
        f[ff.field] = ff.value;
      } else if (op === "does_not_equal") {
        f[ff.field] = { _ne: ff.value };
      } else if (op === "contains") {
        // String contains — use a regex-style search if supported, else skip
        f[ff.field] = { _regex: ff.value };
      } else if (op === "does_not_contain") {
        f[ff.field] = { _notregex: ff.value };
      } else if (op === "before") {
        f[ff.field] = { _lt: ff.value };
      } else if (op === "after") {
        f[ff.field] = { _gt: ff.value };
      } else if (op === "less_than") {
        f[ff.field] = { _lt: ff.value };
      } else if (op === "greater_than") {
        f[ff.field] = { _gt: ff.value };
      }
    }

    return f;
  }, [
    queueId,
    isDefaultView,
    activeFilters.ownerIds,
    activeFilters.status,
    activeFilters.dueDateRange?.from,
    activeFilters.dueDateRange?.to,
    activeFilters.closedDateRange?.from,
    activeFilters.closedDateRange?.to,
    activeFilters.tags,
    activeFilters.freeform,
    stageFilter,
    hasExplicitClosedFilter,
    userId,
  ]);

  const ticketsQuery = useTellescopeInfiniteQuery({
    model: "tickets",
    method: "getSome",
    request: {
      filter: {
        ...ticketsQueryFilter,
        hiddenFromTickets: queueId
          ? undefined
          : {
              _in: [null, false],
            },
      },
      sort: queueId ? "oldFirst" : undefined,
      limit: 1000,
    },
    select: (data) => (queueId ? data.pages.flat() : data.pages.flat().sort((a, b) => (a.index ?? 0) - (b.index ?? 0))),
  });

  const tickets = useMemo(() => {
    const all = ticketsQuery.data ?? [];
    if (queueId || !stageFilter.isMixedSelection) return all;

    return all.filter(
      (ticket) => !!ticket.closedAt || stageFilter.openStages.includes(ticket.stage || NOT_STARTED_STAGE)
    );
  }, [ticketsQuery.data, queueId, stageFilter]);

  const countTicketActiveFilters = useCallback((filters: TicketFilters) => {
    let count = 0;
    if (filters.status && filters.status !== "all") count++;
    if (filters.stages && filters.stages.length > 0) count++;
    if (filters.ownerIds && filters.ownerIds.length > 0) count++;
    if (filters.dueDateRange?.from !== undefined || filters.dueDateRange?.to !== undefined) count++;
    if (filters.closedDateRange?.from !== undefined || filters.closedDateRange?.to !== undefined) count++;
    if (filters.tags && filters.tags.tags.length > 0) count++;
    if (filters.freeform) count += filters.freeform.length;
    return count;
  }, []);

  const viewConfig = useTableViewConfig<TicketFilters>({
    isDefaultView,
    userId,
    selectedView,
    columns,
    allColumnConfigMap: combinedFieldToColumnConfigMap,
    defaultView: DEFAULT_TICKETS_VIEW,
    setUserDefaultView,
    activeFilters,
    countActiveFilters: countTicketActiveFilters,
  });

  const selectedRows = useMemo(() => {
    if (hasSeletedAll) {
      return tickets.map(({ id }) => id);
    }
    return selectedTicketIds;
  }, [hasSeletedAll, selectedTicketIds, tickets]);
  const toggleTicketSelection = useCallback((ticketId: string) => {
    setSelectedTicketIds((prev) => {
      const isAlreadySelected = prev.includes(ticketId);
      return isAlreadySelected ? prev.filter((id) => id !== ticketId) : [ticketId, ...prev];
    });
  }, []);
  const onSelectAll = useCallback(() => {
    setHasSelectedAll((prev) => {
      if (prev) {
        setSelectedTicketIds([]);
      }
      return !prev;
    });
  }, []);

  const handleReorder = useCallback(
    (reorderedItems: Array<{ id: string; index: number }>) => {
      updateTicketIndexesMutation.mutateAsync({
        updates: reorderedItems,
      });
    },
    [updateTicketIndexesMutation]
  );

  const virtualizedTableProps = {
    data: tickets,
    columns,
    getRowId: (row: TicketRecord) => row.id,
    selectedRows,
    onRowSelect: toggleTicketSelection,
    onSelectAll,
    sorting,
    setSorting,
    enableDragDrop: !queueId,
    onReorder: handleReorder,
    onRowClick,
  } satisfies Partial<VirtualizedDataTableProps<TicketRecord>>;

  return {
    views,
    tickets,
    ticketPage: tickets.slice(pageSize * pageIndex, pageSize * pageIndex + pageSize),
    selectedView,
    pageSize,
    setPageSize,
    viewId,
    setViewId,
    pageIndex,
    setPageIndex,
    virtualizedTableProps,
    isLoadingViews,
    ...viewConfig,
  };
};
